import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import ImageIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ImageIconPath";
import { useTranslation } from "@coworker/locales";
import styles from "./AddComment.module.css";
import { AddImagesModal } from "../AddImagesModal/AddImagesModal";
import { useCallback, useMemo, useState } from "react";
import {
  filesToNewFiles,
  NewOrExistingFile,
  newOrExistingFilesToFiles,
} from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import InputWrapper from "../InputWrapper/InputWrapper";
import { MAX_COMMENT_TEXT_LENGTH } from "../../types/Comment";
import { applyRotations } from "@coworker/apprestructured/src/task-management/util";

type AddCommentProps = {
  text: string;
  images: File[];
  disableAllInputs: boolean;
  disableAddButton: boolean;
  onTextChange: (text: string) => void;
  onImagesChange: (images: File[]) => void;
  onAddClick: () => void;
};

export const AddComment = ({
  text,
  images,
  disableAllInputs,
  disableAddButton,
  onTextChange,
  onImagesChange,
  onAddClick,
}: AddCommentProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState<NewOrExistingFile[]>([]);

  const { t } = useTranslation();

  const handleAddImagesButtonClick = useCallback(() => {
    setModalImages(filesToNewFiles(images));
    setIsModalOpen(true);
  }, [images]);

  const handleAddImagesModalConfirmClick = useCallback(() => {
    void (async () => {
      try {
        const processedImages = await applyRotations(modalImages);
        onImagesChange(newOrExistingFilesToFiles(processedImages));
      } catch {
        // Prevent unhandled promise rejections when the modal consumer treats
        // onConfirmClick as a synchronous callback.
      }
    })();
  }, [modalImages, onImagesChange]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const textError = useMemo(() => {
    if (text.length > MAX_COMMENT_TEXT_LENGTH) {
      return t("maximumCharactersLengthString", {
        count: MAX_COMMENT_TEXT_LENGTH,
      });
    }
    return null;
  }, [text, t]);

  const handleAddCommentClick = useCallback(() => {
    if (!textError) {
      onAddClick();
    }
  }, [textError, onAddClick]);

  return (
    <div className={styles["addComment"]}>
      <InputWrapper error={textError}>
        <FixaTextArea
          value={text}
          disabled={disableAllInputs}
          label={t("addCommentString")}
          onChange={(e) => onTextChange(e.target.value)}
        />
      </InputWrapper>
      <div className={styles["buttonWrapper"]}>
        <FixaButton
          size="xsmall"
          text={
            images.length > 0
              ? `${t("addImagesString")} (${images.length})`
              : t("addImagesString")
          }
          type="secondary"
          disabled={disableAllInputs}
          ssrIcon={ImageIconPath}
          onClick={handleAddImagesButtonClick}
        />
        <FixaButton
          size="xsmall"
          text={t("addString")}
          type="emphasised"
          disabled={
            disableAllInputs ||
            disableAddButton ||
            (!text && images.length === 0)
          }
          onClick={handleAddCommentClick}
        />
      </div>
      <AddImagesModal
        isOpen={isModalOpen}
        images={modalImages}
        onImagesChange={setModalImages}
        onClose={handleCloseModal}
        onConfirmClick={async () => {
          await handleAddImagesModalConfirmClick();
        }}
      />
    </div>
  );
};
